/**
 * Authenticated Copilot Studio execution tools.
 *
 * Execution is intentionally NOT read-only: the published agent may invoke a
 * connector, flow, MCP server, or another agent with external side effects.
 * Central annotations must therefore classify this tool as a write operation.
 */

import { z } from "zod";
import {
  COPILOT_AGENT_SCHEMA_NAME_PATTERN,
  COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN,
  COPILOT_CONVERSATION_ID_PATTERN,
  COPILOT_EXECUTION_DELEGATED_PERMISSION,
  COPILOT_EXECUTION_DEFAULT_RESPONSE_CHARS,
  COPILOT_EXECUTION_MAX_ATTACHMENTS,
  COPILOT_EXECUTION_MAX_MESSAGE_CHARS,
  COPILOT_EXECUTION_MAX_RESPONSE_CHARS,
  COPILOT_LOCALE_PATTERN,
  type CopilotStudioExecutionApi,
} from "../api/copilot-studio-execution-api.js";

const environmentIdPattern =
  /^(?:Default-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const nonBlankMessageSchema = z
  .string()
  .min(1)
  .max(COPILOT_EXECUTION_MAX_MESSAGE_CHARS)
  .refine((value) => value.trim().length > 0, "message cannot contain only whitespace");

const httpsUrlSchema = z
  .string()
  .min(1)
  .max(2_048)
  .url()
  .refine((value) => new URL(value).protocol === "https:", "URL must use HTTPS");

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

export const copilotExecutionAttachmentSchema = z
  .object({
    contentType: z
      .string()
      .min(3)
      .max(200)
      .regex(COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN, "contentType must be a MIME type"),
    contentUrl: httpsUrlSchema.optional(),
    content: z.record(z.string().min(1).max(128), z.unknown()).optional(),
    name: z
      .string()
      .min(1)
      .max(255)
      .refine((value) => !hasControlCharacter(value), "name cannot contain control characters")
      .optional(),
    thumbnailUrl: httpsUrlSchema.optional(),
  })
  .strict()
  .refine((value) => value.content !== undefined || value.contentUrl !== undefined, {
    message: "attachment must include content or contentUrl",
  });

export const executeCopilotAgentInputSchema = z
  .object({
    agentSchemaName: z
      .string()
      .regex(
        COPILOT_AGENT_SCHEMA_NAME_PATTERN,
        "agentSchemaName must be the case-sensitive published schema name",
      ),
    message: nonBlankMessageSchema,
    conversationId: z
      .string()
      .regex(
        COPILOT_CONVERSATION_ID_PATTERN,
        "conversationId must be printable ASCII without whitespace",
      )
      .optional(),
    environmentId: z
      .string()
      .regex(environmentIdPattern, "environmentId must be a GUID or Default-{GUID}")
      .optional(),
    locale: z
      .string()
      .regex(COPILOT_LOCALE_PATTERN, "locale must be a BCP-47 language tag")
      .optional(),
    attachments: z
      .array(copilotExecutionAttachmentSchema)
      .max(COPILOT_EXECUTION_MAX_ATTACHMENTS)
      .optional(),
    maxResponseChars: z.number().int().min(1).max(COPILOT_EXECUTION_MAX_RESPONSE_CHARS).optional(),
    timeoutSeconds: z.number().int().min(1).max(180).optional(),
    includeResponseCardDiagnostics: z.boolean().optional(),
  })
  .strict();

export type ExecuteCopilotAgentInput = z.infer<typeof executeCopilotAgentInputSchema>;

const attachmentJsonSchema = {
  type: "object" as const,
  properties: {
    contentType: {
      type: "string",
      minLength: 3,
      maxLength: 200,
      pattern: COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN.source,
      description: "Attachment MIME type, such as application/vnd.microsoft.card.adaptive",
    },
    contentUrl: {
      type: "string",
      format: "uri",
      pattern: "^https://",
      maxLength: 2_048,
      description: "Public HTTPS URL for the attachment",
    },
    content: {
      type: "object",
      description: "Inline JSON attachment content, such as an Adaptive Card",
      additionalProperties: true,
    },
    name: {
      type: "string",
      minLength: 1,
      maxLength: 255,
      description: "Optional attachment name",
    },
    thumbnailUrl: {
      type: "string",
      format: "uri",
      pattern: "^https://",
      maxLength: 2_048,
      description: "Optional public HTTPS thumbnail URL",
    },
  },
  required: ["contentType"],
  anyOf: [{ required: ["content"] }, { required: ["contentUrl"] }],
  additionalProperties: false,
};

export const executeCopilotAgentTool = {
  name: "execute_copilot_agent",
  description: `Execute one turn against a published Copilot Studio agent as the signed-in user through Microsoft's official Direct-to-Engine client. This works for Integrated/Microsoft Entra agents that Direct Line cannot test. Pass conversationId from an earlier call to continue; omit it to force a new conversation. IMPORTANT: this is not read-only — the agent can call connectors, flows, MCP tools, or other agents and those tools may change external systems. completed only means the SDK response iterator returned, not clean service completion or success of the requested action. Bounded endOfConversation protocol signals are reported separately when available; agent text is never classified as an authoritative service status. Requires delegated Power Platform API permission ${COPILOT_EXECUTION_DELEGATED_PERMISSION} and a published agent the signed-in user may use.`,
  inputSchema: {
    type: "object" as const,
    properties: {
      agentSchemaName: {
        type: "string",
        pattern: COPILOT_AGENT_SCHEMA_NAME_PATTERN.source,
        maxLength: 200,
        description:
          "Case-sensitive schema name from Copilot Studio Settings > Advanced > Metadata (for example cr123_myAgent)",
      },
      message: {
        type: "string",
        minLength: 1,
        maxLength: COPILOT_EXECUTION_MAX_MESSAGE_CHARS,
        description: "One user turn. Use conversationId to continue in a later call.",
      },
      conversationId: {
        type: "string",
        minLength: 1,
        maxLength: 512,
        pattern: COPILOT_CONVERSATION_ID_PATTERN.source,
        description:
          "Conversation ID returned by an earlier call. Omit it to force a new conversation.",
      },
      environmentId: {
        type: "string",
        pattern: environmentIdPattern.source,
        description:
          "Agent environment GUID or Default-{GUID}; defaults to the current environment",
      },
      locale: {
        type: "string",
        pattern: COPILOT_LOCALE_PATTERN.source,
        description: "Optional BCP-47 language tag for every turn, such as en-US",
      },
      attachments: {
        type: "array",
        maxItems: COPILOT_EXECUTION_MAX_ATTACHMENTS,
        items: attachmentJsonSchema,
        description:
          "Attachments for this turn. Use inline JSON content or a public HTTPS contentUrl.",
      },
      maxResponseChars: {
        type: "integer",
        minimum: 1,
        maximum: COPILOT_EXECUTION_MAX_RESPONSE_CHARS,
        default: COPILOT_EXECUTION_DEFAULT_RESPONSE_CHARS,
        description:
          "Maximum total agent-response characters returned by this call after secret redaction",
      },
      timeoutSeconds: {
        type: "integer",
        minimum: 1,
        maximum: 180,
        default: 120,
        description: "Maximum wait for each turn (default 120 seconds, maximum 180)",
      },
      includeResponseCardDiagnostics: {
        type: "boolean",
        default: false,
        description:
          "Opt in to bounded, redacted AdaptiveCard text/action diagnostics with start-versus-turn provenance and up to five Markdown links (sanitized HTTPS origin/path only). Omits action data, inputs, raw content, authentication payloads and URL query/fragment/userinfo; never submits or opens a card action. Stream completion does not verify the requested ticket action.",
      },
    },
    required: ["agentSchemaName", "message"],
    additionalProperties: false,
  },
};

export async function handleExecuteCopilotAgent(
  api: CopilotStudioExecutionApi,
  input: unknown,
): Promise<{ text: string; structured: Record<string, unknown> }> {
  const parsed = executeCopilotAgentInputSchema.parse(input);
  const timeoutMs = (parsed.timeoutSeconds ?? 120) * 1_000;
  const result = await api.executeAndWait({
    agentSchemaName: parsed.agentSchemaName,
    message: parsed.message,
    ...(parsed.environmentId !== undefined ? { environmentId: parsed.environmentId } : {}),
    ...(parsed.conversationId !== undefined ? { conversationId: parsed.conversationId } : {}),
    ...(parsed.locale !== undefined ? { locale: parsed.locale } : {}),
    ...(parsed.attachments !== undefined ? { attachments: parsed.attachments } : {}),
    ...(parsed.includeResponseCardDiagnostics !== undefined
      ? { includeResponseCardDiagnostics: parsed.includeResponseCardDiagnostics }
      : {}),
    maxResponseChars: parsed.maxResponseChars ?? COPILOT_EXECUTION_DEFAULT_RESPONSE_CHARS,
    timeoutMs,
  });

  return {
    text:
      "The SDK response iterator ended for the authenticated agent turn. This does not establish " +
      "clean service completion; the requested action remains unverified. Agent tools may have " +
      "produced external side effects; do not blindly retry. " +
      `Conversation ID: ${result.conversationId}. ` +
      `Returned ${result.responses.length} agent message${result.responses.length === 1 ? "" : "s"}.`,
    structured: { execution: result },
  };
}
